import {
  Trash2, Plus, X, ChevronDown, Edit2,
  Building2, Bell, Clock, CreditCard,
  Globe, ShieldCheck, Calendar, Users, Hash, Link as LinkIcon, Info, Check,
  Copy, ExternalLink
} from 'lucide-react';
import { useAlert } from '../components/AlertContext';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { useEffect, useState } from 'react';

export function CompanyPage() {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const [roles, setRoles] = useState([]);
  const [expandedRoles, setExpandedRoles] = useState({});
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', editingId: null });

  const [targetRoleId, setTargetRoleId] = useState(null)

  const [stats, setStats] = useState({
    workingToday: '—',
    avgHours: '—',
    attendanceRate: '—',
  });

  const [shifts, setShifts] = useState([]);
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    name: '',
    startDateString: '09:00',
    endDateString: '17:00',
  });

  const [editingShiftId, setEditingShiftId] = useState(null);
  const [editShiftForm, setEditShiftForm] = useState({
    name: '',
    startDateString: '',
    endDateString: '',
  });

  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      timeOptions.push(`${hour}:${minute}`);
    }
  }

  const [companyId, setCompanyId] = useState(null);

  const [formData, setFormData] = useState({
    telegramChatLink: '',
    telegramChatId: '',
    notifyViaTelegram: false,
    notifyOnCheckIn: false,
    notifyOnCheckOut: false,
    weekends: [6, 7],
    defaultWorkingHours: 8,
    penaltyPerLateMinute: 0,
  });

  const handleToggleChange = (field) => { const newValue = !formData[field]; setFormData((prev) => ({ ...prev, [field]: newValue })); updateCompanyField(field, newValue); };

  const toggleRoleExpand = (roleId) => {
    setExpandedRoles((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  const openRoleModal = (editingRole = null) => {
    if (editingRole) {
      setRoleForm({ name: editingRole.name, editingId: editingRole._id });
    } else {
      setRoleForm({ name: '', editingId: null });
    }
    setIsRoleModalOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      alert(t('roleNameRequired') || 'Role name is required');
      return;
    }
    try {
      let res;
      if (roleForm.editingId) {
        res = await api.put(`/companies/${companyId}/roles/${roleForm.editingId}`, {
          name: roleForm.name.trim(),
        });
      } else {
        res = await api.post(`/companies/${companyId}/roles`, {
          name: roleForm.name.trim(),
        });
      }

      const updatedRole = res.data?.data;
      if (roleForm.editingId) {
        setRoles((prev) =>
          prev.map((r) =>
            r._id === roleForm.editingId ? { ...r, name: updatedRole.name } : r
          )
        );
      } else {
        setRoles((prev) => [...prev, { ...updatedRole, shifts: [] }]);
      }
      setIsRoleModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || t('failedToSaveRole') || 'Failed to save role');
    }
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        setError(null);

        const cid = localStorage.getItem("companyId");
        if (!cid) throw new Error('Company ID not found');
        setCompanyId(cid);

        const companyRes = await api.get(`/companies/${cid}`);
        const companyData = companyRes.data?.data;
        if (!companyData) throw new Error('Company data not found');

        setCompany(companyData);

        setFormData({
          notifyViaTelegram: !!companyData.notifyViaTelegram,
          notifyOnCheckIn: !!companyData.notifyOnCheckIn,
          notifyOnCheckOut: !!companyData.notifyOnCheckOut,
          weekends: companyData.weekends || [6, 7],
          defaultWorkingHours: companyData.defaultWorkingHours ?? 8,
          penaltyPerLateMinute: companyData.penaltyPerLateMinute ?? 0,
        });

        const shiftsRes = await api.get(`/companies/${cid}/shifts`);
        const allShifts = shiftsRes.data?.data || [];

        setShifts(allShifts.filter((s) => !s.roleId));

        const rolesRes = await api.get(`/companies/${cid}/roles`);
        const rawRoles = rolesRes.data?.data || [];

        const rolesWithShifts = rawRoles.map((role) => ({
          ...role,
          shifts: allShifts.filter((s) => s.roleId === role._id),
        }));

        setRoles(rolesWithShifts);
      } catch (err) {
        setError(err.response?.data?.message || t('failedLoadCompanyInfo'));
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, []);

  const updateCompanyField = async (field, value) => {
    if (!companyId) return;
    setSaving(true);

    try {
      const payload = { [field]: value };
      const res = await api.put(`/companies/${companyId}`, payload);

      if (res.data?.data) {
        setCompany((prev) => ({ ...prev, ...res.data.data }));

        setFormData((prev) => ({
          ...prev,
          ...res.data.data,
        }));
      }
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      alert(err.response?.data?.message || t('failedToSaveChanges'));
    } finally {
      setSaving(false);
    }
  };

  const handleWeekendChange = (type) => {
    let weekends = [];

    if (type === "5/2") weekends = [6, 7];
    if (type === "6/1") weekends = [7];

    setFormData((prev) => ({ ...prev, weekends }));
    updateCompanyField("weekends", weekends);
  };

  const handleShiftChange = (e) => {
    setShiftForm({ ...shiftForm, [e.target.name]: e.target.value });
  };

  const handleCreateShift = async () => {
    if (!shiftForm.name.trim()) {
      alert(t('shiftNameRequired'));
      return;
    }

    try {
      const payload = {
        name: shiftForm.name.trim(),
        startDateString: shiftForm.startDateString,
        endDateString: shiftForm.endDateString,
      };

      let res;
      if (targetRoleId) {
        payload.roleId = targetRoleId;                    // ← Shift now has role
        res = await api.post(`/companies/${companyId}/shifts`, payload);

        const newShift = res.data?.data;
        if (newShift) {
          setRoles((prev) =>
            prev.map((role) =>
              role._id === targetRoleId
                ? { ...role, shifts: [...role.shifts, newShift] }
                : role
            )
          );
        }
      } else {
        // General shift (your original logic)
        res = await api.post(`/companies/${companyId}/shifts`, payload);
        const newShift = res.data?.data;
        if (newShift) setShifts([...shifts, newShift]);
      }

      setShiftForm({ name: '', startDateString: '09:00', endDateString: '17:00' });
      setIsShiftModalOpen(false);
      setTargetRoleId(null);
    } catch (err) {
      alert(err.response?.data?.message || t('failedCreateShift'));
    }
  };

  const handleDeleteRoleShift = async (roleId, shiftId) => {
    if (!window.confirm(t('deleteShiftConfirm'))) return;

    try {
      await api.delete(`/companies/${companyId}/shifts/${shiftId}`);
      setRoles((prev) =>
        prev.map((role) =>
          role._id === roleId
            ? { ...role, shifts: role.shifts.filter((s) => s._id !== shiftId) }
            : role
        )
      );
    } catch (err) {
      alert(err.response?.data?.message || t('failedDeleteShift'));
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm(t('deleteShiftConfirm'))) return;

    try {
      await api.delete(`/companies/${companyId}/shifts/${shiftId}`);
      setShifts(shifts.filter((s) => s._id !== shiftId));
    } catch (err) {
      alert(err.response?.data?.message || t('failedDeleteShift'));
    }
  };

  const handleStartEditShift = (shift) => {
    setEditingShiftId(shift._id);
    setEditShiftForm({
      name: shift.name,
      startDateString: shift.startDateString,
      endDateString: shift.endDateString,
    });
  };

  const handleCancelEditShift = () => {
    setEditingShiftId(null);
    setEditShiftForm({ name: '', startDateString: '', endDateString: '' });
  };

  const handleSaveEditShift = async (shiftId, roleId = null) => {
    if (!editShiftForm.name.trim()) {
      alert(t('shiftNameRequired'));
      return;
    }

    try {
      const payload = {
        name: editShiftForm.name.trim(),
        startDateString: editShiftForm.startDateString,
        endDateString: editShiftForm.endDateString,
      };

      const res = await api.put(`/companies/${companyId}/shifts/${shiftId}`, payload);
      const updatedShift = res.data?.data;

      if (updatedShift) {
        if (roleId) {
          setRoles((prev) =>
            prev.map((role) =>
              role._id === roleId
                ? {
                  ...role,
                  shifts: role.shifts.map((s) =>
                    s._id === shiftId ? updatedShift : s
                  ),
                }
                : role
            )
          );
        } else {
          setShifts((prev) =>
            prev.map((s) => (s._id === shiftId ? updatedShift : s))
          );
        }
      }
      setEditingShiftId(null);
    } catch (err) {
      alert(err.response?.data?.message || t('failedUpdateShift'));
    }
  };

  const getDaysLeft = () => {
    if (!company?.subscription?.expiresAt) return '—';
    const exp = new Date(company.subscription.expiresAt);
    const now = new Date();
    const diff = exp - now;
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) return <div className="p-10 text-center text-gray-300">Loading...</div>;
  if (error) return <div className="p-10 text-center text-red-400">{error}</div>;
  if (!company) return <div className="p-10 text-center text-gray-500">{t('noCompanyData')}</div>;

  const handleCopyCardNum = () => {
    navigator.clipboard.writeText('4444555566667777');
    showAlert(t('cardNumberCopied'), 'success');
  };

  const sub = company.subscription || {};
  const daysLeft = getDaysLeft();

  return (
    <div className="p-4 sm:p-6 text-white min-h-screen bg-transparent space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-600/20 rounded-xl">
            <Building2 className="w-8 h-8 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{company.name}</h1>
            <p className="text-gray-400 text-sm mt-0.5 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> {company.location || 'No location set'}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto self-stretch sm:self-auto" onClick={() => setIsPaymentModalOpen(true)} style={{ cursor: 'pointer' }} >
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 bg-gray-800/40 backdrop-blur-md border border-gray-700/50 p-4 sm:px-6 sm:py-3.5 rounded-2xl shadow-xl no-scrollbar overflow-x-auto w-full sm:w-auto">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 shrink-0">
              {/* Plan Info */}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg shrink-0">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 uppercase font-black tracking-widest leading-none mb-1">{t('plan')}</div>
                  <div className="text-sm font-bold text-white flex items-center gap-2">
                    {sub.plan || 'Standard'}
                    <span className="text-[11px] text-blue-400/80 font-medium px-2 py-0.5 bg-blue-500/10 rounded-full border border-blue-500/20">
                      {sub.price?.toLocaleString()} USD
                    </span>
                  </div>
                </div>
              </div>

              <div className="hidden sm:block w-px h-10 bg-gray-700/50"></div>

              {/* Status Info */}
              <div>
                <div className="text-[10px] text-gray-500 uppercase font-black tracking-widest leading-none mb-1">{t('status')}</div>
                <div className="flex items-center gap-2">
                  {sub.isActive ? (
                    <span className="flex items-center gap-1.5 text-xs text-green-400 font-black uppercase tracking-wider bg-green-500/10 px-2.5 py-1 rounded-lg border border-green-500/20">
                      <ShieldCheck className="w-3.5 h-3.5" /> {t('subscriptionActive')}
                    </span>
                  ) : (
                    <span className="text-xs text-yellow-400 font-black uppercase tracking-wider bg-yellow-500/10 px-2.5 py-1 rounded-lg border border-yellow-500/20">
                      {t('subscriptionInActive')}
                    </span>
                  )}
                </div>
              </div>

              <div className="hidden sm:block w-px h-10 bg-gray-700/50"></div>

              {/* Countdown Info */}
              <div>
                <div className="text-[10px] text-gray-500 uppercase font-black tracking-widest leading-none mb-1">{t('daysLeft')}</div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-lg font-black leading-none ${daysLeft <= 7 ? 'text-yellow-400 animate-pulse' : 'text-blue-400'}`}>
                    {daysLeft}
                  </span>
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-widest">{t('days')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
        {/* Main Settings Column */}
        <div className="xl:col-span-2 space-y-8">

          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-800/40 p-5 rounded-2xl border border-gray-700/50 hover:border-gray-600/50 transition-colors group">
              <div className="flex items-center gap-3 mb-2 text-gray-500 group-hover:text-blue-400 transition-colors">
                <Hash className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">System ID</span>
              </div>
              <div className="text-lg font-mono font-bold text-gray-200">{company.systemId || '—'}</div>
            </div>

            <div className="bg-gray-800/40 p-5 rounded-2xl border border-gray-700/50 hover:border-gray-600/50 transition-colors group">
              <div className="flex items-center gap-3 mb-2 text-gray-500 group-hover:text-blue-400 transition-colors">
                <Users className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">{t('companySize')}</span>
              </div>
              <div className="text-lg font-bold text-gray-200">{company.companySize || '0'} {t('employees')}</div>
            </div>

            <div className="bg-gray-800/40 p-5 rounded-2xl border border-gray-700/50 hover:border-gray-600/50 transition-colors group">
              <div className="flex items-center gap-3 mb-2 text-gray-500 group-hover:text-blue-400 transition-colors">
                <Calendar className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">{t('created')}</span>
              </div>
              <div className="text-lg font-bold text-gray-200">
                {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : '—'}
              </div>
            </div>

            <div className="bg-gray-800/40 p-5 rounded-2xl border border-gray-700/50 hover:border-gray-600/50 transition-colors group overflow-hidden">
              <div className="flex items-center gap-3 mb-2 text-gray-500 group-hover:text-blue-400 transition-colors">
                <LinkIcon className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Webhook</span>
              </div>
              <div className="text-sm font-semibold text-gray-400 truncate" title={company.webhookUrl}>
                {company.webhookUrl || 'Not set'}
              </div>
            </div>
          </div>

          <div className="bg-gray-800/40 rounded-3xl border border-gray-700/50 overflow-hidden backdrop-blur-sm">
            <div className="px-6 py-5 border-b border-gray-700/50 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Bell className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-bold text-white">{t('telegramNotificationSettings')}</h2>
              </div>
              <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${formData.notifyViaTelegram ? 'bg-blue-500/10 text-blue-400' : 'bg-gray-700/50 text-gray-500'}`}>
                {formData.notifyViaTelegram ? 'Active' : 'Disabled'}
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid sm:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start justify-between p-4 bg-gray-900/40 rounded-2xl border border-gray-700/30">
                    <div className="max-w-[70%]">
                      <div className="font-bold text-blue-100 text-sm mb-1">{t('enableTelegramNotifications')}</div>
                      <p className="text-xs text-gray-500 leading-relaxed">{t('receiveAttendanceUpdatesViaTelegram')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer mt-1">
                      <input type="checkbox" checked={formData.notifyViaTelegram} onChange={() => handleToggleChange('notifyViaTelegram')} className="sr-only peer" disabled={saving} />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-start justify-between p-4 bg-gray-900/40 rounded-2xl border border-gray-700/30">
                    <div className="max-w-[70%]">
                      <div className="font-bold text-blue-100 text-sm mb-1">{t('notifyOnCheckIn')}</div>
                      <p className="text-xs text-gray-500 leading-relaxed">{t('getNotifiedWhenEmployeeChecksIn')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer mt-1">
                      <input type="checkbox" checked={formData.notifyOnCheckIn} onChange={() => handleToggleChange('notifyOnCheckIn')} className="sr-only peer" disabled={saving} />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-start justify-between p-4 bg-gray-900/40 rounded-2xl border border-gray-700/30">
                    <div className="max-w-[70%]">
                      <div className="font-bold text-blue-100 text-sm mb-1">{t('notifyOnCheckOut')}</div>
                      <p className="text-xs text-gray-500 leading-relaxed">{t('getNotifiedWhenEmployeeChecksOut')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer mt-1">
                      <input type="checkbox" checked={formData.notifyOnCheckOut} onChange={() => handleToggleChange('notifyOnCheckOut')} className="sr-only peer" disabled={saving} />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                <div className="bg-blue-600/5 rounded-3xl p-6 border border-blue-500/10 flex flex-col justify-center text-center">
                  <div className="p-4 bg-blue-500/10 rounded-full w-fit mx-auto mb-4">
                    <Bell className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{t('telegramChatId')}</h3>
                  <div className="text-blue-400 font-mono text-sm break-all bg-blue-500/10 px-4 py-3 rounded-2xl border border-blue-500/20 mb-4 select-all">
                    {company.telegramNotificationsChatId || t('na')}
                  </div>
                  <a
                    href={company.telegramNotificationsChatLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-gray-400 hover:text-white underline underline-offset-4 transition-colors truncate px-2"
                  >
                    {company.telegramNotificationsChatLink || 'No link configured'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Settings Column */}
        <div className="space-y-8">
          <div className="bg-gray-800/40 rounded-3xl border border-gray-700/50 backdrop-blur-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-700/50 flex items-center gap-2.5">
              <Clock className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-bold text-white">{t('baseSettings')}</h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest block pl-1">{t('workingDays')}</label>
                <div className="flex gap-2 p-1.5 bg-gray-900/60 rounded-2xl border border-gray-700/30">
                  <button
                    onClick={() => handleWeekendChange("5/2")}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${JSON.stringify(formData.weekends) === JSON.stringify([6, 7]) ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-gray-500 hover:text-white hover:bg-gray-800"}`}
                  >
                    5 / 2
                  </button>
                  <button
                    onClick={() => handleWeekendChange("6/1")}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${JSON.stringify(formData.weekends) === JSON.stringify([7]) ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-gray-500 hover:text-white hover:bg-gray-800"}`}
                  >
                    6 / 1
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest block pl-1">{t('defaultWorkingHours')}</label>
                <div className="relative group">
                  <input
                    type="number"
                    className="w-full bg-gray-900/60 border border-gray-700/50 rounded-2xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500/50 transition-all font-bold group-hover:bg-gray-900"
                    value={formData.defaultWorkingHours}
                    onChange={(e) => setFormData((prev) => ({ ...prev, defaultWorkingHours: Number(e.target.value) }))}
                    onBlur={() => updateCompanyField("defaultWorkingHours", formData.defaultWorkingHours)}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-600 font-bold uppercase pointer-events-none">Hours</div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest block pl-1">{t('penaltyRate')}</label>
                <div className="relative group">
                  <input
                    type="number"
                    className="w-full bg-gray-900/60 border border-gray-700/50 rounded-2xl px-4 py-3.5 text-white focus:outline-none focus:border-red-500/50 transition-all font-bold group-hover:bg-gray-900"
                    value={formData.penaltyPerLateMinute}
                    onChange={(e) => setFormData((prev) => ({ ...prev, penaltyPerLateMinute: Number(e.target.value) }))}
                    onBlur={() => updateCompanyField("penaltyPerLateMinute", formData.penaltyPerLateMinute)}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-red-900/50 font-bold uppercase pointer-events-none">UZS / MIN</div>
                </div>
                <div className="flex items-start gap-2 pt-1 pl-1">
                  <Info className="w-3.5 h-3.5 text-gray-600 shrink-0 mt-0.5" />
                  <p className="text-[10px] text-gray-600 italic">This rate will be used for automated late calculation adjustments.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shifts Section */}
      <div className="bg-gray-800 rounded-none sm:rounded-2xl shadow overflow-hidden">
        <div className="p-5 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
          <h2 className="text-xl font-semibold">{t('workShifts')}</h2>

          <button
            onClick={() => {
              setTargetRoleId(null);
              setShiftForm({ name: '', startDateString: '09:00', endDateString: '17:00' });
              setIsShiftModalOpen(true);
            }}
            className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-sky-900/20 transition-all duration-200 active:scale-95 font-medium w-full sm:w-auto"
          >
            <Plus size={16} className="sm:w-[18px] sm:h-[18px]" /> {t('addShift')}
          </button>
        </div>

        <div className="p-6">
          {shifts.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {t('noShiftsDefined')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-700 text-gray-300">
                  <tr>
                    <th className="px-6 py-4 text-left">{t('shiftName')}</th>
                    <th className="px-6 py-4 text-left">{t('startTime')}</th>
                    <th className="px-6 py-4 text-left">{t('endTime')}</th>
                    <th className="px-6 py-4 text-center">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift) => (
                    <tr
                      key={shift._id}
                      className="border-t border-gray-700 hover:bg-gray-900/50 transition"
                    >
                      {editingShiftId === shift._id ? (
                        <>
                          <td className="px-6 py-4">
                            <input
                              type="text"
                              value={editShiftForm.name}
                              onChange={(e) => setEditShiftForm({ ...editShiftForm, name: e.target.value })}
                              className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={editShiftForm.startDateString}
                              onChange={(e) => setEditShiftForm({ ...editShiftForm, startDateString: e.target.value })}
                              className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none"
                            >
                              {timeOptions.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={editShiftForm.endDateString}
                              onChange={(e) => setEditShiftForm({ ...editShiftForm, endDateString: e.target.value })}
                              className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none"
                            >
                              {timeOptions.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() => handleSaveEditShift(shift._id)}
                                className="text-green-500 hover:text-green-400 p-1 rounded-full hover:bg-green-500/10 transition"
                                title={t('save')}
                              >
                                <Check size={18} />
                              </button>
                              <button
                                onClick={handleCancelEditShift}
                                className="text-gray-400 hover:text-gray-300 p-1 rounded-full hover:bg-gray-700 transition"
                                title={t('cancel')}
                              >
                                <X size={18} />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 font-medium">{shift.name}</td>
                          <td className="px-6 py-4">{shift.startDateString}</td>
                          <td className="px-6 py-4">{shift.endDateString}</td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() => handleStartEditShift(shift)}
                                className="text-blue-400 hover:text-blue-300 transition"
                                title={t('editShift')}
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteShift(shift._id)}
                                className="text-red-500 hover:text-red-400 transition"
                                title={t('deleteShift')}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ==================== NEW: ROLE BASED SHIFTS SECTION ==================== */}
      <div className="bg-gray-800 rounded-none sm:rounded-2xl shadow overflow-hidden">
        <div className="p-5 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
          <h2 className="text-xl font-semibold">{t('roleBasedShifts') || 'Role Based Shifts'}</h2>
          <button
            onClick={() => openRoleModal()}
            className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-sky-900/20 transition-all duration-200 active:scale-95 font-medium w-full sm:w-auto"
          >
            <Plus size={16} className="sm:w-[18px] sm:h-[18px]" /> {t('addNewRole') || 'Add Role'}
          </button>
        </div>

        <div className="p-6 space-y-4">
          {roles.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {t('noRolesDefined') || 'No roles defined yet. Add a role to get started.'}
            </div>
          ) : (
            roles.map((role) => {
              const isExpanded = expandedRoles[role._id] || false;
              return (
                <div key={role._id} className="border border-gray-700 rounded-xl overflow-hidden">
                  {/* Role Header */}
                  <div
                    className="flex items-center justify-between p-5 bg-gray-900 cursor-pointer hover:bg-gray-800"
                    onClick={() => toggleRoleExpand(role._id)}
                  >
                    <div className="text-base sm:text-lg font-medium text-white">{role.name}</div>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openRoleModal(role);
                        }}
                        className="text-blue-400 hover:text-blue-300"
                        title="Edit role"
                      >
                        <Edit2 size={18} />
                      </button>
                      <div className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown size={20} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Shifts */}
                  {isExpanded && (
                    <div className="p-5">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 sm:gap-0">
                        <h3 className="font-medium text-gray-300">{t('shiftsForRole')}</h3>
                        <button
                          onClick={() => {
                            setTargetRoleId(role._id);
                            setShiftForm({ name: '', startDateString: '09:00', endDateString: '17:00' });
                            setIsShiftModalOpen(true);
                          }}
                          className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-3 py-1.5 rounded-xl shadow-lg shadow-sky-900/20 transition-all duration-200 active:scale-95 font-medium text-xs sm:text-sm w-full sm:w-auto"
                        >
                          <Plus size={14} className="sm:w-4 sm:h-4" /> {t('addShift')}
                        </button>
                      </div>

                      {role.shifts.length === 0 ? (
                        <div className="text-center text-gray-500 py-6 border border-dashed border-gray-700 rounded-xl">
                          {t('noShiftsFoundForRole')}
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-700 text-gray-300">
                              <tr>
                                <th className="px-6 py-4 text-left">{t('shiftName')}</th>
                                <th className="px-6 py-4 text-left">{t('startTime')}</th>
                                <th className="px-6 py-4 text-left">{t('endTime')}</th>
                                <th className="px-6 py-4 text-center">{t('actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {role.shifts.map((shift) => (
                                <tr key={shift._id} className="border-t border-gray-700 hover:bg-gray-900/50 transition">
                                  {editingShiftId === shift._id ? (
                                    <>
                                      <td className="px-6 py-4">
                                        <input
                                          type="text"
                                          value={editShiftForm.name}
                                          onChange={(e) => setEditShiftForm({ ...editShiftForm, name: e.target.value })}
                                          className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none"
                                        />
                                      </td>
                                      <td className="px-6 py-4">
                                        <select
                                          value={editShiftForm.startDateString}
                                          onChange={(e) => setEditShiftForm({ ...editShiftForm, startDateString: e.target.value })}
                                          className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none"
                                        >
                                          {timeOptions.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="px-6 py-4">
                                        <select
                                          value={editShiftForm.endDateString}
                                          onChange={(e) => setEditShiftForm({ ...editShiftForm, endDateString: e.target.value })}
                                          className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none"
                                        >
                                          {timeOptions.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center gap-3">
                                          <button
                                            onClick={() => handleSaveEditShift(shift._id, role._id)}
                                            className="text-green-500 hover:text-green-400 p-1 rounded-full hover:bg-green-500/10 transition"
                                            title={t('save')}
                                          >
                                            <Check size={18} />
                                          </button>
                                          <button
                                            onClick={handleCancelEditShift}
                                            className="text-gray-400 hover:text-gray-300 p-1 rounded-full hover:bg-gray-700 transition"
                                            title={t('cancel')}
                                          >
                                            <X size={18} />
                                          </button>
                                        </div>
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-6 py-4 font-medium">{shift.name}</td>
                                      <td className="px-6 py-4">{shift.startDateString}</td>
                                      <td className="px-6 py-4">{shift.endDateString}</td>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center gap-3">
                                          <button
                                            onClick={() => handleStartEditShift(shift)}
                                            className="text-blue-400 hover:text-blue-300 transition"
                                            title={t('editShift')}
                                          >
                                            <Edit2 size={18} />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteRoleShift(role._id, shift._id)}
                                            className="text-red-500 hover:text-red-400"
                                          >
                                            <Trash2 size={18} />
                                          </button>
                                        </div>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add Shift Modal */}
      {
        isShiftModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl p-5 shadow-2xl relative max-h-[90vh] overflow-y-auto w-full sm:w-[400px]">
              <button
                onClick={() => {
                  setIsShiftModalOpen(false);
                  setTargetRoleId(null);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>

              <h2 className="text-lg sm:text-xl font-bold mb-4 text-blue-400 pr-8">
                {targetRoleId
                  ? `Add Shift for ${roles.find((r) => r._id === targetRoleId)?.name}`
                  : t('addNewShift')}
              </h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    {t('shiftName')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={shiftForm.name}
                    onChange={handleShiftChange}
                    placeholder={t('shiftNamePlaceholderExample')}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">
                      {t('startTime')}
                    </label>
                    <select
                      name="startDateString"
                      value={shiftForm.startDateString}
                      onChange={handleShiftChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                    >
                      {timeOptions.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-2">
                      {t('endTime')}
                    </label>
                    <select
                      name="endDateString"
                      value={shiftForm.endDateString}
                      onChange={handleShiftChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                    >
                      {timeOptions.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreateShift}
                className="mt-5 w-full bg-blue-600 hover:bg-blue-700 py-2 rounded-xl text-sm font-semibold transition"
              >
                {t('createShift')}
              </button>
            </div>
          </div>
        )
      }

      {
        isRoleModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl p-5 shadow-2xl relative max-h-[90vh] overflow-y-auto w-full sm:w-[400px]">
              <button
                onClick={() => setIsRoleModalOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>

              <h2 className="text-lg sm:text-xl font-bold mb-4 text-blue-400 pr-8">
                {roleForm.editingId ? t('editRole') || 'Edit Role' : t('addNewRole') || 'Add New Role'}
              </h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">{t('roleName')}</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={t('roleNamePlaceHolder')}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveRole}
                className="mt-5 w-full bg-blue-600 hover:bg-blue-700 py-2 rounded-xl text-sm font-semibold transition"
              >
                {roleForm.editingId ? t('saveChanges') : t('apply')}
              </button>
            </div>
          </div>
        )
      }
      {/* Payment Modal */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl w-full max-w-md p-6 border border-gray-700 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600/20 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white">{t('paymentCredentials')}</h3>
              </div>
              <button
                onClick={() => setIsPaymentModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="text-gray-300 text-sm leading-relaxed space-y-2">
                <p className="font-semibold text-blue-400">{t('paymentInstructions')}</p>
                <p>{t('paymentStep1')}</p>

                {/* Card Display */}
                <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 flex items-center justify-between group mt-3">
                  <div className="font-mono text-lg font-bold tracking-[0.2em] text-white">
                    5614 6825 0545 1460
                  </div>
                  <button
                    onClick={handleCopyCardNum}
                    className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-blue-400 transition-all active:scale-90"
                    title={t('copyCardNumber')}
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>

                <p>{t('paymentStep2')}</p>
                <p>{t('paymentStep3')}</p>
              </div>

              <div className="pt-4 flex flex-col gap-3">
                <a
                  href="https://t.me/pyonic"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-3 rounded-xl font-bold transition-all shadow-lg shadow-sky-900/20 active:scale-95"
                >
                  <ExternalLink className="w-5 h-5" />
                  {t('sendReceipt')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
      <div className="text-gray-400 text-xs sm:text-sm mb-1">{title}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}